"use client";

import { motion } from "framer-motion";
import { PortableText, toPlainText } from "@portabletext/react";
import { GroqImage } from "@/helpers/Image";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { competitorRichText } from "./richtext";
import { PageCompetitorSchema } from "@/sanity/pages/PageCompetitorTemplate";

const ease = [0.25, 0.1, 0.25, 1] as const;

export default function FounderSection({ data }: { data: PageCompetitorSchema }) {
  const credentials = data.founderCredentials ?? [];
  const mobileImage = data.founderImageMobile ?? data.founderImage;

  if (!data.founderTitle && !data.founderImage) return null;

  return (
    <section className="w-full bg-[#f3f3f3] px-[2rem] [text-wrap:pretty] py-[7rem] lg:px-0 lg:py-[15rem]">
      <div className="mx-auto flex w-[min(150rem,100%)] flex-col gap-[6rem] lg:flex-row lg:items-center lg:justify-between lg:gap-[4rem]">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, ease }}
          className="flex w-full flex-col gap-[2.25rem] lg:w-[min(75.75rem,100%)] lg:shrink-0 lg:gap-[3rem]">
          <div className="flex flex-col gap-[2.5rem] max-lg:items-center max-lg:text-center lg:items-start lg:gap-[3rem]">
            {data.founderChip && (
              <div className="flex items-center gap-[1.25rem] border border-black/[0.04] bg-black/[0.04] px-[2.5rem] py-[1.5rem]">
                <span aria-hidden className="h-[0.75rem] w-[0.75rem] shrink-0 rounded-full bg-[#0d2922]" />
                <p className="mb-0 font-geist text-[1.75rem] font-medium leading-[1.146] text-black/[0.64] uppercase [text-box:trim-both_cap_alphabetic]">
                  {data.founderChip}
                </p>
              </div>
            )}
            {data.founderTitle && (
              <div className="richtext max-lg:[text-wrap:balance] lg:richtext-start lg:items-start lg:text-left prose-headings:my-0 prose-headings:font-blacker prose-headings:font-normal prose-headings:leading-[1.15] prose-headings:text-black max-lg:prose-headings:text-[4.5rem] max-lg:prose-headings:tracking-[-0.225rem] lg:prose-headings:w-[min(67.125rem,100%)] lg:prose-headings:text-[6rem] lg:prose-headings:tracking-[-0.3rem] [--hl-wash:4.5rem] lg:[--hl-wash:5.75rem] [--hl-tracking:-0.05em] [&_:is(h1,h2)_span]:leading-[0] [&_span:has(>.hl-wash)]:!text-[#3a5842]">
                <PortableText value={data.founderTitle} components={competitorRichText} />
              </div>
            )}
          </div>

          {mobileImage && (
            <div className="w-full rounded-[1rem] border border-black/[0.16] p-[0.75rem] lg:hidden">
              <GroqImage
                src={mobileImage}
                alt={data.founderTitle ? toPlainText(data.founderTitle) : ""}
                quality={100}
                className="!h-auto !w-full aspect-[385/350] rounded-[0.5rem] object-cover object-top"
              />
            </div>
          )}

          {credentials.length > 0 && (
            <ul className="mb-0 flex flex-col gap-[2rem] pl-0">
              {credentials.map((item, index) => (
                <li key={item._key ?? index} className="flex list-none flex-col gap-[2rem]">
                  <div className="flex items-center gap-[4.875rem]">
                    {item.icon && (
                      <GroqImage src={item.icon} alt="" quality={100} className="!h-[3.25rem] !w-[3.25rem] shrink-0 object-contain" />
                    )}
                    {item.text && (
                      <div className="richtext richtext-start flex-1 items-start text-left prose-p:my-0 prose-p:font-geist prose-p:!text-[2.25rem] prose-p:leading-[1.5] prose-p:!text-black/70 [&_strong]:font-medium [&_strong]:text-black">
                        <PortableText value={item.text} components={richTextComponent} />
                      </div>
                    )}
                  </div>
                  <div className="h-px w-full bg-black/10" />
                </li>
              ))}
            </ul>
          )}
        </motion.div>

        {data.founderImage && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8, ease, delay: 0.1 }}
            className="w-[min(70.625rem,100%)] shrink-0 rounded-[1rem] border border-black/[0.16] p-[0.75rem] max-lg:hidden">
            <GroqImage
              src={data.founderImage}
              alt={data.founderTitle ? toPlainText(data.founderTitle) : ""}
              quality={100}
              className="!h-auto !w-full rounded-[0.5rem]"
            />
          </motion.div>
        )}
      </div>
    </section>
  );
}
